import { useTranslation } from 'react-i18next';

import { GROUPS } from '@/service/groups';

const group = GROUPS.ophub;

/**
 * 租户费用中心总览（菜单 title 必须与 tenantMenu 一致：consoleOverview）
 *
 * 页面：`pages/tenant/overview`
 * - 总览：一键授予本页接口（summary / 图表 / 收支明细）
 * - 立即充值：本页按钮；依赖 pre-create、system-config、状态轮询与取消
 * - 订单 / 账单 / 充值记录 Tag：归属独立菜单配置，与总览互不依赖；页内入口复用其 list
 * - 权益中心：见 `useBenefitsCenter`，勿再挂在总览下
 */
export function useWsChargeOverview() {
  const { t } = useTranslation();

  /** 总览页核心接口依赖（对齐 DC revenue-analysis 子资源结构） */
  const overviewApiDeps = {
    [`["${group}"].cost-analysis/summary.get`]: true,
    [`["${group}"].cost-analysis/category-proportions.get`]: true,
    [`["${group}"].cost-analysis/time-series.get`]: true,
    [`["${group}"].transactions.list`]: true,
  } as const;

  const overviewApiUncheck = {
    [`["${group}"].cost-analysis/summary.get`]: undefined,
    [`["${group}"].cost-analysis/category-proportions.get`]: undefined,
    [`["${group}"].cost-analysis/time-series.get`]: undefined,
    [`["${group}"].transactions.list`]: undefined,
  } as const;

  /** 立即充值弹窗依赖 */
  const rechargeCreateDeps = {
    [`["${group}"].recharge-requests/pre-create.create`]: true,
    [`["${group}"].system-config.get`]: true,
    // 微信充值：轮询 GET recharge-requests/:id；取消 POST .../cancel
    [`["${group}"].recharge-requests.get`]: true,
    [`["${group}"].recharge-requests/cancel.create`]: true,
  } as const;

  const rechargeCreateUncheck = {
    [`["${group}"].recharge-requests/pre-create.create`]: undefined,
    [`["${group}"].system-config.get`]: undefined,
    [`["${group}"].recharge-requests.get`]: undefined,
    [`["${group}"].recharge-requests/cancel.create`]: undefined,
  } as const;

  return {
    title: 'consoleOverview',
    list: [
      {
        group,
        list: [
          {
            resource: 'cost-analysis',
            type: 'get',
            relative: {
              onCheck: {
                ...overviewApiDeps,
              },
              onUnCheck: {
                ...overviewApiUncheck,
              },
            },
            name: <span>get: {t('auth-conf:overview')}</span>,
          },
          {
            resource: 'cost-analysis/summary',
            type: 'get',
            hidden: true,
            name: <span>get: {t('tenant:account.overview')}</span>,
          },
          {
            resource: 'cost-analysis/category-proportions',
            type: 'get',
            hidden: true,
            name: <span>get: {t('common:consumption.category')}</span>,
          },
          {
            resource: 'cost-analysis/time-series',
            type: 'get',
            hidden: true,
            name: <span>get: {t('tenant:consumption.overview')}</span>,
          },
          {
            resource: 'transactions',
            type: 'list',
            hidden: true,
            name: <span>list: {t('hooks:transaction.details')}</span>,
          },
          // 立即充值（概览页按钮）
          {
            type: 'create',
            resource: 'recharge-requests',
            relative: {
              onCheck: {
                ...rechargeCreateDeps,
              },
              onUnCheck: {
                ...rechargeCreateUncheck,
              },
            },
            name: <span>create: {t('order:recharge.now')}</span>,
          },
          {
            resource: 'recharge-requests/pre-create',
            type: 'create',
            hidden: true,
            name: <span>create: {t('common:recharge')} ID</span>,
          },
          {
            resource: 'system-config',
            type: 'get',
            hidden: true,
            name: <span>get: {t('auth-conf:get.operation.configuration')}</span>,
          },
          {
            resource: 'recharge-requests',
            type: 'get',
            hidden: true,
            name: <span>get: {t('common:recharge')}</span>,
          },
          {
            resource: 'recharge-requests/cancel',
            type: 'create',
            hidden: true,
            name: <span>create: {t('common:cancel')}</span>,
          },
        ],
      },
    ],
  };
}

/**
 * 租户权益中心（菜单 title: benefitsCenter）
 *
 * 页面：`pages/tenant/cost-center/benefits-center`
 * - GET user-coupons → list
 * - POST coupons/redeem → create
 */
export function useBenefitsCenter() {
  const { t } = useTranslation();
  return {
    group,
    title: 'benefitsCenter',
    list: [
      {
        list: [
          {
            resource: 'user-coupons',
            type: 'list',
            relative: {
              onUnCheck: {
                [`["${group}"].coupons/redeem.create`]: undefined,
              },
            },
            name: <span>list: {t('auth-conf:coupon.list')}</span>,
          },
          {
            resource: 'coupons/redeem',
            type: 'create',
            relative: {
              onCheck: {
                [`["${group}"].user-coupons.list`]: true,
              },
            },
            name: <span>create: {t('tenant:coupon.redeem')}</span>,
          },
        ],
      },
    ],
  };
}

/**
 * 数据中心 - 运营中心总览（菜单 title 必须与 bizCenterMenu 一致：consoleOverview）
 *
 * 页面：`pages/data-center/cost-center/income-detail`
 * - 总览：一键授予本页接口（summary / 图表 / 收支明细）
 * - 订单列表、账单列表：归属 `useOrderManagement` / `useBillingManagement`，与总览互不依赖；
 *   总览页内跳转入口直接复用上述菜单的 list 权限
 */
export function useDcChargeOverview() {
  const { t } = useTranslation();

  /** 总览页核心接口依赖 */
  const overviewApiDeps = {
    [`["${group}"].revenue-analysis/summary.get`]: true,
    [`["${group}"].revenue-analysis/category-proportions.get`]: true,
    [`["${group}"].revenue-analysis/time-series.get`]: true,
    [`["${group}"].datacenter-transactions.list`]: true,
  } as const;

  const overviewApiUncheck = {
    [`["${group}"].revenue-analysis/summary.get`]: undefined,
    [`["${group}"].revenue-analysis/category-proportions.get`]: undefined,
    [`["${group}"].revenue-analysis/time-series.get`]: undefined,
    [`["${group}"].datacenter-transactions.list`]: undefined,
  } as const;

  return {
    // 与 hooks/useMenu/bizCenterMenu 总览菜单 title 对齐，否则权限弹窗显示「无需权限配置」
    title: 'consoleOverview',
    // 路由 title 仍为 chargeOverview，一并匹配以免遗漏
    titles: ['chargeOverview'],
    list: [
      {
        group,
        list: [
          {
            resource: 'revenue-analysis',
            type: 'get',
            relative: {
              onCheck: {
                ...overviewApiDeps,
              },
              onUnCheck: {
                ...overviewApiUncheck,
              },
            },
            name: <span>get: {t('auth-conf:overview')}</span>,
          },
          {
            resource: 'revenue-analysis/summary',
            type: 'get',
            hidden: true,
            name: <span>get: {t('data-center:cost.center.account.overview')}</span>,
          },
          {
            resource: 'revenue-analysis/category-proportions',
            type: 'get',
            hidden: true,
            name: <span>get: {t('auth-conf:revenue.category.proportions')}</span>,
          },
          {
            resource: 'revenue-analysis/time-series',
            type: 'get',
            hidden: true,
            name: <span>get: {t('auth-conf:revenue.time.series')}</span>,
          },
          {
            resource: 'datacenter-transactions',
            type: 'list',
            hidden: true,
            name: <span>list: {t('hooks:transaction.details')}</span>,
          },
        ],
      },
    ],
  };
}
